import React, { useState, useEffect, useRef } from 'react';
import { ChromePicker } from 'react-color';
import QRCodeCard from '../../components/qr/QRCodeCard';
import { useTheme } from '../../context/ThemeContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const QRGenerator = () => {
  const { isDark } = useTheme();
  const { user, hasPermission } = useAdminAuth();
  const { t } = useTranslation('admin');
  const [foregroundColor, setForegroundColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [size, setSize] = useState(256);
  const [errorCorrection, setErrorCorrection] = useState('M');
  const [showFgPicker, setShowFgPicker] = useState(false);
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [contrastWarning, setContrastWarning] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoSize, setLogoSize] = useState(60); // Size of logo in pixels
  const fileInputRef = useRef(null);
  const [frameText, setFrameText] = useState('Scan to Book Your Appointment');
  const [poweredByText, setPoweredByText] = useState('Powered by Hairchitech');
  const [shopName, setShopName] = useState('');
  const [showFrame, setShowFrame] = useState(true);

  // Calculate contrast and show warning
  useEffect(() => {
    const getLuminance = (color) => {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16) / 255;
      const g = parseInt(hex.substr(2, 2), 16) / 255;
      const b = parseInt(hex.substr(4, 2), 16) / 255;
      
      const sRGB = [r, g, b].map(c => 
        c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
      );
      
      return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];
    };

    const fgLuminance = getLuminance(foregroundColor);
    const bgLuminance = getLuminance(backgroundColor);
    const contrast = (Math.max(fgLuminance, bgLuminance) + 0.05) / (Math.min(fgLuminance, bgLuminance) + 0.05);
    
    setContrastWarning(contrast < 2);
  }, [foregroundColor, backgroundColor]);

  // Build QR URL
  const buildQRUrl = () => {
    // Get current origin (localhost in dev, production domain in prod)
    const origin = window.location.origin;

    // CRA-compatible env var (REACT_APP_*). Falls back to default path
    const bookingPath = user?.shop_public_booking_id
      ? `/book/${user.shop_public_booking_id}`
      : (process.env.REACT_APP_BOOKING_PATH || '/app/book');

    // Build base URL
    const finalUrl = `${origin}${bookingPath}?utm_source=qr`;

    return finalUrl;
  };

  const qrUrl = buildQRUrl();

  const isBackgroundTransparent = backgroundColor === 'transparent' || backgroundColor === 'rgba(0,0,0,0)';

  // Handle logo file upload
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert(t('admin.qr_select_image_file'));
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(t('admin.qr_file_size_limit'));
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove logo
  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };


  // Check if user has permission to access QR Generator
  if (!hasPermission('settings')) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t('admin.access_denied')}</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('admin.qr_no_permission')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 16a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1v-4zM13 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V4zM13 16a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4zM6 6h2v2H6V6zM6 18h2v2H6v-2zM18 6h2v2h-2V6zM18 18h2v2h-2v-2zM9 9h2v2H9V9zM9 15h2v2H9v-2zM15 9h2v2h-2V9zM15 15h2v2h-2v-2zM12 12h2v2h-2v-2z" />
              </svg>
              {t('admin.qr_page_title')}
            </h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              {t('admin.qr_page_subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <Card className="mb-6">
          <CardHeader
            title={t('admin.qr_configuration')}
            subtitle={t('admin.qr_configuration_subtitle')}
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
          />
          <div className="p-4 md:p-5">


            {/* Logo Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">{t('admin.qr_logo_optional')}</label>
              <div className="space-y-3">
                {!logoPreview ? (
                  <div className="flex items-center space-x-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                    >
                      {t('admin.qr_choose_file')}
                    </motion.button>
                    <span className="text-sm text-zinc-500 dark:text-zinc-400">{t('admin.qr_no_file_chosen')}</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-3 p-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-12 h-12 object-cover rounded-lg border border-white/20 dark:border-white/10"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-zinc-600 dark:text-zinc-400">{logoFile.name}</p>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={removeLogo}
                        className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                      >
                        {t('admin.remove')}
                      </motion.button>
                    </div>
                  </div>
                )}
                
                {logoPreview && (
                  <div>
                    <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">{t('admin.qr_logo_size')}</label>
                    <input
                      type="range"
                      min="40"
                      max="120"
                      value={logoSize}
                      onChange={(e) => setLogoSize(parseInt(e.target.value))}
                      className="w-full h-2 bg-zinc-200 dark:bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                      style={{ accentColor: ACCENT }}
                    />
                    <div className="flex justify-between text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                      <span>40px</span>
                      <span>{logoSize}px</span>
                      <span>120px</span>
                    </div>
                    {logoSize > 80 ? (
                      <div className="mt-2 p-3 bg-orange-100 dark:bg-orange-900/30 rounded-xl text-xs text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800">
                        ⚠️ <strong>{t('admin.warning')}:</strong> {t('admin.qr_logo_large_warning')}
                      </div>
                    ) : (
                      <div className="mt-2 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-xs text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                        💡 <strong>{t('admin.tip')}:</strong> {t('admin.qr_logo_tip')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Color Pickers */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">{t('admin.qr_colors')}</label>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Foreground Color */}
                <div>
                  <label className="block text-sm text-zinc-600 dark:text-zinc-400 mb-2">
                    {t('admin.qr_foreground_color')}
                  </label>
                  <div className="flex items-center space-x-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={() => setShowFgPicker(!showFgPicker)}
                    className="w-12 h-8 border-2 border-white/20 dark:border-white/10 rounded-lg cursor-pointer transition-all hover:border-[--accent]/30"
                    style={{ backgroundColor: foregroundColor, '--accent': ACCENT }}
                    />
                    <span className="text-sm font-mono text-zinc-700 dark:text-zinc-300">{foregroundColor}</span>
                  </div>
                  {showFgPicker && (
                    <div className="absolute z-10 mt-2">
                      <div 
                        className="fixed inset-0" 
                        onClick={() => setShowFgPicker(false)}
                      />
                      <ChromePicker
                        color={foregroundColor}
                        onChange={(color) => setForegroundColor(color.hex)}
                        disableAlpha
                      />
                    </div>
                  )}
                </div>

                {/* Background Color */}
                <div>
                  <label className="block text-sm text-zinc-600 dark:text-zinc-400 mb-2">
                    {t('admin.qr_background_color')}
                  </label>
                  <div className="flex items-center space-x-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={() => setShowBgPicker(!showBgPicker)}
                    className="w-12 h-8 border-2 border-white/20 dark:border-white/10 rounded-lg cursor-pointer transition-all hover:border-[--accent]/30"
                    style={{ backgroundColor: backgroundColor, '--accent': ACCENT }}
                    />
                    <span className="text-sm font-mono text-zinc-700 dark:text-zinc-300">{backgroundColor}</span>
                  </div>
                  {showBgPicker && (
                    <div className="absolute z-10 mt-2">
                      <div 
                        className="fixed inset-0" 
                        onClick={() => setShowBgPicker(false)}
                      />
                      <ChromePicker
                        color={backgroundColor}
                        onChange={(color) => setBackgroundColor(color.hex)}
                        disableAlpha
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Validation Warnings */}
              {isBackgroundTransparent && (
                <div className="mb-4 p-3 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-400 dark:border-yellow-800 rounded-xl">
                  <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                    ⚠️ {t('admin.qr_transparent_warning')}
                  </p>
                </div>
              )}

              {contrastWarning && !isBackgroundTransparent && (
                <div className="mb-4 p-3 bg-orange-100 dark:bg-orange-900/30 border border-orange-400 dark:border-orange-800 rounded-xl">
                  <p className="text-orange-800 dark:text-orange-300 text-sm">
                    ⚠️ {t('admin.qr_low_contrast_warning')}
                  </p>
                </div>
              )}
            </div>

            {/* Size and Error Correction */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">{t('admin.qr_size')}</label>
                <select
                  value={size}
                  onChange={(e) => setSize(parseInt(e.target.value))}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
                >
                  <option value={128}>128px</option>
                  <option value={256}>256px</option>
                  <option value={512}>512px</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">{t('admin.qr_error_correction')}</label>
                <select
                  value={errorCorrection}
                  onChange={(e) => setErrorCorrection(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
                >
                  <option value="L">L ({t('admin.qr_ec_low')})</option>
                  <option value="M">M ({t('admin.qr_ec_medium')})</option>
                  <option value="Q">Q ({t('admin.qr_ec_quartile')})</option>
                  <option value="H">H ({t('admin.qr_ec_high')})</option>
                </select>
              </div>
            </div>

            {/* Frame and Branding */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('admin.qr_frame_branding')}</label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showFrame}
                    onChange={(e) => setShowFrame(e.target.checked)}
                  className="rounded border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 text-[--accent] focus:ring-[--accent]/20"
                  style={{ '--accent': ACCENT }}
                  />
                  <span className="text-sm text-zinc-600 dark:text-zinc-400">{t('admin.qr_show_frame')}</span>
                </label>
              </div>
              
              {showFrame && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">{t('admin.qr_main_text')}</label>
                    <input
                      type="text"
                      value={frameText}
                      onChange={(e) => setFrameText(e.target.value)}
                      placeholder={t('admin.qr_frame_text_placeholder')}
                      className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all text-sm"
                      style={{ '--accent': ACCENT }}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">{t('admin.qr_powered_by_text')}</label>
                    <input
                      type="text"
                      value={poweredByText}
                      onChange={(e) => setPoweredByText(e.target.value)}
                      placeholder={t('admin.qr_powered_by_placeholder')}
                      className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all text-sm"
                      style={{ '--accent': ACCENT }}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">{t('admin.qr_shop_name_optional')}</label>
                    <input
                      type="text"
                      value={shopName}
                      onChange={(e) => setShopName(e.target.value)}
                      placeholder={t('admin.qr_shop_name_placeholder')}
                      className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all text-sm"
                      style={{ '--accent': ACCENT }}
                    />
                  </div>
                </div>
              )}
            </div>

          </div>
        </Card>

        {/* QR Code Preview */}
        <Card>
          <CardHeader
            title={t('admin.qr_preview_title')}
            subtitle={t('admin.qr_preview_subtitle')}
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            }
          />
          <div className="p-4 md:p-5 flex justify-center">
            <QRCodeCard
              value={qrUrl}
              fg={foregroundColor}
              bg={isBackgroundTransparent ? '#FFFFFF' : backgroundColor}
              size={size}
              level={errorCorrection}
              logo={logoPreview}
              logoSize={logoSize}
              frameText={frameText}
              poweredByText={poweredByText}
              shopName={shopName}
              showFrame={showFrame}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default QRGenerator;
